'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  onCtaClick?: () => void;
  ctaVariant?: 'primary' | 'secondary';
  scrollThreshold?: number;
}

const Header = ({ 
  onCtaClick, 
  ctaVariant = 'primary',
  scrollThreshold = 50 
}: HeaderProps) => {
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const scrollPercentage = (scrollPosition / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      setIsScrolled(scrollPosition > 20);
      setShowStickyCta(scrollPercentage > scrollThreshold);
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollThreshold, mounted]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else {
      scrollToSection('get-access');
    }
  };

  const navigationItems = [
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'assessment', label: 'Screen Time Assessment' },
    { id: 'get-access', label: 'Get Access' }
  ];

  return (
    <>
      <header data-component-id="src/components/common/Header.tsx:75:6" data-component-path="src/components/common/Header.tsx" data-component-line="75" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D" 
        className={`fixed top-0 left-0 right-0 z-header transition-smooth ${
          mounted && isScrolled 
            ? 'bg-background/95 backdrop-blur-sm shadow-card' 
            : 'bg-transparent'
        }`}
      >
        <div data-component-id="src/components/common/Header.tsx:82:8" data-component-path="src/components/common/Header.tsx" data-component-line="82" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-5%20lg%3Apx-10%22%7D" className="container mx-auto px-5 lg:px-10">
          <div data-component-id="src/components/common/Header.tsx:83:10" data-component-path="src/components/common/Header.tsx" data-component-line="83" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-16%20lg%3Ah-20%22%7D" className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link data-component-id="src/components/common/Header.tsx:85:12" data-component-path="src/components/common/Header.tsx" data-component-line="85" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20transition-smooth%20hover%3Aopacity-80%22%7D" 
              href="/"
              className="flex items-center space-x-2 transition-smooth hover:opacity-80"
            >
              <div data-component-id="src/components/common/Header.tsx:89:14" data-component-path="src/components/common/Header.tsx" data-component-line="89" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-20%20w-32%20h-32%20lg%3Aw-40%20lg%3Ah-40%20rounded-lg%20flex%20items-center%20justify-center%22%7D" className="mt-20 w-32 h-32 lg:w-40 lg:h-40 rounded-lg flex items-center justify-center">
  <img data-component-id="src/components/common/Header.tsx:90:2" data-component-path="src/components/common/Header.tsx" data-component-line="90" data-component-file="Header.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fassets%2Fimages%2FChatGPT_Image_Dec_3_2025_07_38_02_PM-1764793182688.png%22%2C%22alt%22%3A%22ScrollSmart%20mascot%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%22%7D" 
    src="/assets/images/ChatGPT_Image_Dec_3_2025_07_38_02_PM-1764793182688.png" 
    alt="ScrollSmart mascot"
    className="w-full h-full object-contain"
  />
              </div>
             
            </Link>

            {/* Desktop Navigation */}
            <nav data-component-id="src/components/common/Header.tsx:100:12" data-component-path="src/components/common/Header.tsx" data-component-line="100" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20space-x-1%20lg%3Aspace-x-2%22%7D" className="hidden md:flex items-center space-x-1 lg:space-x-2">
              {navigationItems.slice(0, 3).map((item) => (
                <button data-component-id="src/components/common/Header.tsx:102:16" data-component-path="src/components/common/Header.tsx" data-component-line="102" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-3%20lg%3Apx-4%20py-2%20text-sm%20lg%3Atext-base%20font-medium%20text-text-secondary%20hover%3Atext-primary%20transition-smooth%20rounded-lg%20hover%3Abg-muted%22%7D"
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="px-3 lg:px-4 py-2 text-sm lg:text-base font-medium text-text-secondary hover:text-primary transition-smooth rounded-lg hover:bg-muted"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div data-component-id="src/components/common/Header.tsx:113:12" data-component-path="src/components/common/Header.tsx" data-component-line="113" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20space-x-3%22%7D" className="hidden md:flex items-center space-x-3">
              <button data-component-id="src/components/common/Header.tsx:114:14" data-component-path="src/components/common/Header.tsx" data-component-line="114" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20lg%3Apx-6%20py-2%20g%3Apy-2.5%20text-sm%20lg%3Atext-base%20font-semibold%20text-white%20rounded-lg%20transition-smooth%20shadow-cta%20bg-gradient-to-r%20from-%5B%231cb7d6%5D%2F80%20to-%5B%23138aa3%5D%2F80%20hover%3Afrom-%5B%2318a3bf%5D%2F80%20hover%3Ato-%5B%230f748a%5D%2F80%22%2C%22textContent%22%3A%22Get%20Early%20Access%22%7D"
                onClick={handleCtaClick}
               className="px-4 lg:px-6 py-2 g:py-2.5 text-sm lg:text-base font-semibold text-white rounded-lg transition-smooth shadow-cta bg-gradient-to-r from-[#1cb7d6]/80 to-[#138aa3]/80 hover:from-[#18a3bf]/80 hover:to-[#0f748a]/80"
              >
                Get Early Access
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button data-component-id="src/components/common/Header.tsx:123:12" data-component-path="src/components/common/Header.tsx" data-component-line="123" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22md%3Ahidden%20p-2%20text-text-secondary%20hover%3Atext-primary%20transition-smooth%22%7D"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-text-secondary hover:text-primary transition-smooth"
              aria-label="Toggle menu"
            >
              <Icon data-component-id="src/components/common/Header.tsx:128:14" data-component-path="src/components/common/Header.tsx" data-component-line="128" data-component-file="Header.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" 
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
                size={24} 
                variant="outline"
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mounted && isMobileMenuOpen && (
          <div data-component-id="src/components/common/Header.tsx:139:10" data-component-path="src/components/common/Header.tsx" data-component-line="139" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20bg-background%20border-t%20border-border%20shadow-elevated%20animate-slide-up%22%7D" className="md:hidden bg-background border-t border-border shadow-elevated animate-slide-up">
            <nav data-component-id="src/components/common/Header.tsx:140:12" data-component-path="src/components/common/Header.tsx" data-component-line="140" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22container%20mx-auto%20px-5%20py-4%20space-y-1%22%7D" className="container mx-auto px-5 py-4 space-y-1">
              {navigationItems.map((item) => (
                <button data-component-id="src/components/common/Header.tsx:142:16" data-component-path="src/components/common/Header.tsx" data-component-line="142" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20text-left%20px-4%20py-3%20text-base%20font-medium%20text-text-secondary%20hover%3Atext-primary%20hover%3Abg-muted%20rounded-lg%20transition-smooth%22%7D"
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="w-full text-left px-4 py-3 text-base font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-lg transition-smooth"
                >
                  {item.label}
                </button>
              ))}
              <button data-component-id="src/components/common/Header.tsx:150:14" data-component-path="src/components/common/Header.tsx" data-component-line="150" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20text-base%20font-semibold%20text-white%20rounded-lg%20transition-smooth%20shadow-cta%20mt-2%20bg-gradient-to-r%20from-%5B%231cb7d6%5D%2F80%20to-%5B%23138aa3%5D%2F80%20hover%3Afrom-%5B%2318a3bf%5D%2F80%20hover%3Ato-%5B%230f748a%5D%2F80%22%2C%22textContent%22%3A%22Get%20Early%20Access%22%7D"
                onClick={handleCtaClick}
               className="w-full px-4 py-3 text-base font-semibold text-white rounded-lg transition-smooth shadow-cta mt-2 bg-gradient-to-r from-[#1cb7d6]/80 to-[#138aa3]/80 hover:from-[#18a3bf]/80 hover:to-[#0f748a]/80"
              >
                Get Early Access
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Sticky CTA */}
      {mounted && showStickyCta && (
        <div data-component-id="src/components/common/Header.tsx:163:8" data-component-path="src/components/common/Header.tsx" data-component-line="163" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20fixed%20bottom-0%20left-0%20right-0%20z-header%20bg-background%2F95%20backdrop-blur-sm%20border-t%20border-border%20shadow-elevated%20pb-safe-bottom%20animate-slide-up%22%7D" className="md:hidden fixed bottom-0 left-0 right-0 z-header bg-background/95 backdrop-blur-sm border-t border-border shadow-elevated pb-safe-bottom animate-slide-up">
          <div data-component-id="src/components/common/Header.tsx:164:10" data-component-path="src/components/common/Header.tsx" data-component-line="164" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-5%20py-3%22%7D" className="container mx-auto px-5 py-3">
            <button data-component-id="src/components/common/Header.tsx:165:12" data-component-path="src/components/common/Header.tsx" data-component-line="165" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-3%20text-base%20font-semibold%20text-white%20rounded-lg%20transition-smooth%20shadow-cta%20bg-gradient-to-r%20from-purple-700%2F80%20to-purple-900%2F80%20hover%3Afrom-purple-800%2F80%20hover%3Ato-purple-950%2F80%22%2C%22textContent%22%3A%22Get%20Early%20Access%22%7D"
              onClick={handleCtaClick}
              className="w-full px-6 py-3 text-base font-semibold text-white rounded-lg transition-smooth shadow-cta bg-gradient-to-r from-purple-700/80 to-purple-900/80 hover:from-purple-800/80 hover:to-purple-950/80"
            >
              Get Early Access
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;